import type { Match2Reward } from '../match2/rewards/match2-rewards.ruleset';

/**
 * The themes available for rescue levels
 */
export const rescueLevelThemes = [
  'crocodile',
  'drowning',
  'fire',
  'frozen',
] as const;

export type RescueLevelTheme = (typeof rescueLevelThemes)[number];

/**
 * The possible results of a rescue level
 */
export type RescueLevelResult = 'win' | 'skip';

/**
 * The rescue level configuration type
 */
export type RescueLevelConfig = {
  theme: RescueLevelTheme;
  levelData: string;
  timeout: number;
  reward: Match2Reward;
  level: number;
};

type RescueLevelLayoutConfig = Omit<RescueLevelConfig, 'theme'>;
type RescueLevelConfigDefaults = Omit<RescueLevelLayoutConfig, 'levelData'>;

/**
 * Shared configuration defaults for rescue levels.
 */
const rescueLevelConfigDefaults: RescueLevelConfigDefaults = {
  timeout: 45000,
  reward: { type: 'gems', amount: 10 },
  level: 0,
};

/**
 * The layouts randomly available to each rescue level.
 */
const rescueLevelLayoutData = [
  `m40o[ha16]s[bb,bg,br,by]g[o9r9c[rs,rs,rs,rs,rs,rs,ha,ha,,rs,rs,rs,rs,rs,rs,ha,ha,,rs,rs,rs,rs,rs,rs,ha,ha,,rs,rs,rs,rs,rs,rs,ha,ha,,rs,rs,rs,rs,rs,rs,ha,ha,,rs,rs,rs,rs,rs,rs,ha,ha,,rs,rs,rs,rs,rs,rs,ha,ha,,rs,rs,rs,rs,rs,rs,ha,ha,,,,,,,,,,]]`,
  `m40o[ha17]s[bb,bg,by]g[o9r9c[ha,by,br,bg,bb,bg,br,by,ha,by,ha,by,bg,bb,bg,by,ha,by,br,by,ha,by,bb,by,ha,by,br,bg,bg,by,ha,br,ha,by,bg,bg,bb,bb,bb,br,ha,br,bb,bb,bb,bg,bg,by,ha,br,ha,by,bg,bg,br,by,ha,by,bb,by,ha,by,br,by,ha,by,bg,bb,bg,by,ha,by,ha,by,br,bg,bb,bg,br,by,ha]]`,
  `m40o[ha20]s[bb,bp,bg]g[o9r9c[bb,bp,bg,ha,ha,ha,bb,bp,bg,bb,bp,ha,bb,bp,bg,ha,bp,bg,bp,ha,bb,bp,bg,bb,bp,ha,bb,ha,bg,bb,bp,bg,bb,bp,bg,ha,ha,bb,bp,bg,bb,bp,bg,bb,ha,ha,bb,bp,bg,bb,bp,bg,bb,ha,bb,ha,bg,bb,bp,bg,bb,ha,bg,bb,bp,ha,bb,bp,bg,ha,bp,bg,bp,bg,bb,ha,ha,ha,bp,bg,bb]]`,
  `m40o[ha16]s[bb,bp,bg]g[o9r9c[bb,bp,bg,bb,ha,bg,bb,bp,bg,bb,bp,bg,ha,bp,ha,bb,bp,bg,bp,bg,ha,bp,bg,bb,ha,bg,bb,bp,ha,bb,bp,bg,bb,bp,ha,bb,ha,bb,bp,bg,bb,bp,bg,bb,ha,bg,ha,bp,bg,bb,bp,bg,ha,bp,bb,bp,ha,bb,bp,bg,ha,bp,bg,bb,bp,bg,ha,bp,ha,bb,bp,bg,bp,bg,bb,bp,ha,bb,bp,bg,bb]]`,
  `m40o[ha20]s[br,by,bb]g[o9r9c[br,ha,ha,ha,by,bb,br,by,bb,ha,by,bb,br,ha,bb,br,by,bb,ha,bb,br,by,bb,ha,by,bb,br,by,ha,br,by,bb,br,ha,bb,br,bb,br,ha,bb,br,by,bb,ha,by,bb,ha,by,bb,br,by,ha,br,by,ha,by,bb,br,by,ha,br,by,bb,ha,by,bb,br,ha,bb,br,by,bb,by,ha,ha,ha,bb,br,by,bb,br]]`,
  `m40o[ha16]s[bb,bg,br,by]g[o9r9c[rs,rs,rs,rs,ha,ha,rs,rs,,rs,rs,rs,rs,ha,ha,rs,rs,,rs,rs,rs,rs,ha,ha,rs,rs,,rs,rs,rs,rs,ha,ha,rs,rs,,rs,rs,rs,rs,ha,ha,rs,rs,,rs,rs,rs,rs,ha,ha,rs,rs,,rs,rs,rs,rs,ha,ha,rs,rs,,rs,rs,rs,rs,ha,ha,rs,rs,,,,,,,,,,]]`,
  `m40o[ha16]s[bb,bg,br,by]g[o9r9c[rs,rs,ha,ha,rs,rs,rs,rs,,rs,rs,ha,ha,rs,rs,rs,rs,,rs,rs,ha,ha,rs,rs,rs,rs,,rs,rs,ha,ha,rs,rs,rs,rs,,rs,rs,ha,ha,rs,rs,rs,rs,,rs,rs,ha,ha,rs,rs,rs,rs,,rs,rs,ha,ha,rs,rs,rs,rs,,rs,rs,ha,ha,rs,rs,rs,rs,,,,,,,,,,]]`,
] as const;

export const rescueLevelConfigs: readonly RescueLevelLayoutConfig[] =
  rescueLevelLayoutData.map((levelData) => ({
    ...rescueLevelConfigDefaults,
    levelData,
  }));

/**
 * The first rescue theme for each OA campaign.
 */
export const rescueLevelFirstThemes = {
  oa_fire: 'fire',
  oa_drowning: 'drowning',
  oa_crocodile: 'crocodile',
  oa_frozen: 'frozen',
} as const satisfies Record<string, RescueLevelTheme>;

export const rescueLevelOABuckets = [
  'oa_crocodile',
  'oa_drowning',
  'oa_fire',
  'oa_frozen',
] as const satisfies readonly (keyof typeof rescueLevelFirstThemes)[];

export type RescueLevelOABucket = (typeof rescueLevelOABuckets)[number];

export function isRescueLevelOABucket(
  bucket: string | null | undefined,
): bucket is RescueLevelOABucket {
  return rescueLevelOABuckets.some((oaBucket) => oaBucket === bucket);
}

/**
 * The number of levels gap between scheduled rescue levels
 */
export const rescueLevelInterval = 10;

/** The number of rescue levels in each OA campaign. */
export const rescueLevelsPerSequence = 2;
